import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import formatUSD from '../../../utils/formatUSD';
import cartUtils from '../../../utils/cart';
import styled from 'styled-components';

const OrderBox = styled.div`
  border: 1px solid #ccc;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-top: 0;
  text-align: center;
`;

const OrderHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 24px;
`;

const SummarySubtotal = styled.div`
  display: flex;
  flex-direction: column; // Keep this to stack lines vertically
  margin-bottom: 16px;
`;

const SummaryLine = styled.div`
  display: flex; // This will keep items in this line next to each other
  justify-content: space-between; // This will maintain the spacing between the text and price
  margin-bottom: 8px; // Space between lines, adjust as needed
`;

const SummaryText = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const SummaryPrice = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const CalculationsMessage = styled.div`
  font-weight: normal;
  font-style: italic;
  padding: 4px 0 0 0;
  text-align: center;
`;

const StyledLink = styled(Link)`
  display: inline-block;
  margin-top: 24px;
  padding: 10px 16px;
  background-color: #479de6;
  font-weight: 900;
  color: #fff;
  text-align: center;
  text-decoration: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.1s ease;
  width: 200px;
  font-size: 16px;

  &:hover {
    color: #fff;
    background-color: #357ab8;
  }
`;

const DisabledLink = styled.div`
  display: inline-block;
  margin-top: 24px;
  padding: 10px 16px;
  background-color: #ccc;
  font-weight: 900;
  color: #fff;
  text-align: center;
  text-decoration: none;
  border-radius: 4px;
  cursor: not-allowed;
  width: 200px;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  color: #d9534f;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
  font-weight: normal;
`;

const OrderSummary = (props) => {
  const { orders } = props;
  const backgroundsPrice = cartUtils.sumUniqueBackgroundPrices(orders);
  const hasOnlyRetouching = cartUtils.anyOrderContainsOnlyRetouching(orders);
  const cartVoucherItems = useSelector((state) => state.cartVoucherItems);
  const voucherSubtotal = cartVoucherItems.voucherSubtotalCents || 0;
  const country = useSelector((s) => s.parent?.address?.country) || 'US';
  const violatingItems = cartUtils.internationalShippingViolations(
    orders,
    country,
  );

  return (
    <OrderBox>
      <OrderHeader>Order Summary</OrderHeader>

      <SummarySubtotal>
        <SummaryLine>
          <SummaryText>Products Subtotal</SummaryText>
          <SummaryPrice>
            {formatUSD(cartUtils.calculateTotalPrice(orders))}
          </SummaryPrice>
        </SummaryLine>
        {backgroundsPrice > 0 && (
          <SummaryLine>
            <SummaryText>Backgrounds Subtotal</SummaryText>
            <SummaryPrice>{formatUSD(backgroundsPrice)}</SummaryPrice>
          </SummaryLine>
        )}
        {voucherSubtotal > 0 && (
          <SummaryLine>
            <SummaryText>Vouchers Subtotal</SummaryText>
            <SummaryPrice>{formatUSD(voucherSubtotal)}</SummaryPrice>
          </SummaryLine>
        )}
      </SummarySubtotal>

      <CalculationsMessage>
        Shipping, tax, and discounts will be calculated during checkout.
      </CalculationsMessage>

      {violatingItems.length > 0 ? (
        <>
          <DisabledLink id="proceed-to-checkout-disabled">
            Proceed to Checkout
          </DisabledLink>
          <ErrorMessage>
            These items can't ship to addresses outside the US:
            <ul>
              {violatingItems.map((item) => (
                <li key={item.id || item.productName}>{item.productName}</li>
              ))}
            </ul>
            Remove them from your cart, or{' '}
            <Link to="/v3/checkout/customer-address-edit">
              update your address
            </Link>{' '}
            to a US address to continue.
          </ErrorMessage>
        </>
      ) : hasOnlyRetouching ? (
        <>
          <DisabledLink id="proceed-to-checkout-disabled">
            Proceed to Checkout
          </DisabledLink>
          <ErrorMessage>
            Retouching cannot be ordered as a standalone product. Please add
            other products to your cart.
          </ErrorMessage>
        </>
      ) : (
        <StyledLink to="/v3/checkout" id="proceed-to-checkout">
          Proceed to Checkout
        </StyledLink>
      )}
    </OrderBox>
  );
};

export default OrderSummary;
